import { useTranslation } from 'react-i18next';
import { DocumentIcon } from '@/utils/icons';
import { ContextMenu } from '../../ContextMenu/ContextMenu';
import { useDocumentViewModel, type IDocumentViewModelProps } from '../hooks/useDocumentViewModel';
import { FileCard } from './FileCard';
import { FolderCard } from './FolderCard';
import { NewFolderCard } from './NewFolderCard';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './DocumentGrid.module.css';

export interface IDocumentGridProps extends IDocumentViewModelProps {
  /** Null while the working site resolves — cards then show glyphs only. */
  siteId: string | null;
}

/**
 * The folder's contents as cards — the table's alternative renderer, over the
 * same data, the same selection, and the same three behaviours (the sort
 * order, inline naming, the right-click menu) from useDocumentViewModel.
 * Order is the table's default: folders A→Z, then files newest first; the
 * grid has no column headers to re-sort by.
 */
export function DocumentGrid({ siteId, ...props }: IDocumentGridProps) {
  const { parentFolder = null, emptyMessage } = props;
  const { t } = useTranslation();
  const { sorted, sortedFolders, isEmpty, menu, openFolder, folderProps, fileProps, newFolderProps } =
    useDocumentViewModel(props);

  const emptyState = (
    <>
      <span className={styles.emptyIcon}><DocumentIcon size={32} /></span>
      <span>{emptyMessage ?? t('documents.emptyFolder')}</span>
    </>
  );

  return (
    <ScrollArea
      orientation="both"
      className={`${styles.gridWrap} ${menu.ctxMenu ? styles.menuOpen : ''}`}
      viewportClassName={styles.gridBody}
      viewportProps={{ onContextMenu: menu.openBackgroundMenu }}
    >
      {isEmpty && parentFolder === null ? (
        <div className={styles.empty}>{emptyState}</div>
      ) : (
        // An explicit list: `list-style: none` strips the role in some
        // readers, and the count of items is worth announcing.
        <ul className={styles.grid} role="list" aria-label={t('documents.view.grid')}>
          {parentFolder !== null && (
            <li><FolderCard folder={parentFolder} isParent onOpen={openFolder} /></li>
          )}
          {newFolderProps && <li><NewFolderCard {...newFolderProps} /></li>}
          {sortedFolders.map((folder) => <li key={folder.id}><FolderCard {...folderProps(folder)} /></li>)}
          {sorted.map((doc) => <li key={doc.id}><FileCard siteId={siteId} {...fileProps(doc)} /></li>)}
          {isEmpty && <li className={styles.empty}>{emptyState}</li>}
        </ul>
      )}

      {menu.ctxMenu && (
        <ContextMenu x={menu.ctxMenu.x} y={menu.ctxMenu.y} items={menu.ctxItems} onClose={menu.closeCtxMenu} />
      )}
    </ScrollArea>
  );
}
